import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface NotificationItem {
  id?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  module: string;
  body: {
    body: string;
    title: string;
    description: string;
  };
  description: string;
  url: string;
  read: boolean;
  made_at: {
    id: string;
    name: string;
    job_title: string;
    color: string;
    signature: string;
  };
  job_center: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationStateService {
  private notificationsSubject = new BehaviorSubject<NotificationItem[]>([]);
  private hasUnreadNotificationsSubject = new BehaviorSubject<boolean>(false);

  public notifications$ = this.notificationsSubject.asObservable();
  public hasUnreadNotifications$ = this.hasUnreadNotificationsSubject.asObservable();

  constructor() {}

  getNotifications(): NotificationItem[] {
    return this.notificationsSubject.value;
  }

  setNotifications(notifications: NotificationItem[]): void {
    this.notificationsSubject.next(notifications);
    this.updateUnreadStatus(notifications);
  }

  addNotification(notification: NotificationItem): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = [notification, ...currentNotifications];
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadStatus(updatedNotifications);
  }

  markAsRead(notificationId: string): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification =>
      notification.id === notificationId
        ? { ...notification, read: true }
        : notification
    );
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadStatus(updatedNotifications);
  }

  markAllAsRead(): void {
    const currentNotifications = this.notificationsSubject.value;
    const updatedNotifications = currentNotifications.map(notification => ({
      ...notification,
      read: true
    }));
    this.notificationsSubject.next(updatedNotifications);
    this.updateUnreadStatus(updatedNotifications);
  }

  getNotificationsWithId(): NotificationItem[] {
    return this.notificationsSubject.value.filter(notification => notification.id);
  }

  private updateUnreadStatus(notifications: NotificationItem[]): void {
    const hasUnread = notifications.some(notification => !notification.read);
    this.hasUnreadNotificationsSubject.next(hasUnread);
  }

  clearNotifications(): void {
    this.notificationsSubject.next([]);
    this.hasUnreadNotificationsSubject.next(false);
  }
}
